import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERFIS_KEYS,
  atualizarPerfil,
  buscarMatriz,
  criarPerfil,
  excluirPerfil,
  listarPerfis,
  salvarMatriz,
  type AtualizarPerfilPayload,
  type CriarPerfilPayload,
  type PermissionPatch,
} from '../api';
import { PERMISSIONS_QUERY_KEY } from '../../../shared/hooks/usePermissions';
import { ROLE_OPTIONS_QUERY_KEY } from '../../../shared/hooks/useRoleLabels';

export function usePerfis() {
  return useQuery({ queryKey: PERFIS_KEYS.list(), queryFn: listarPerfis });
}

export function useMatriz(roleId: string | null) {
  return useQuery({
    queryKey: PERFIS_KEYS.matrix(roleId ?? ''),
    queryFn: () => buscarMatriz(roleId!),
    enabled: Boolean(roleId),
  });
}

/**
 * Invalida também as permissões da própria sessão e os rótulos: o
 * Administrador pode estar editando o perfil que ele mesmo usa, e a tela
 * precisa refletir isso sem exigir novo login.
 */
function useInvalidateAfterWrite() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: PERFIS_KEYS.all });
    queryClient.invalidateQueries({ queryKey: PERMISSIONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ROLE_OPTIONS_QUERY_KEY });
  };
}

export function useCriarPerfil() {
  const invalidate = useInvalidateAfterWrite();
  return useMutation({
    mutationFn: (payload: CriarPerfilPayload) => criarPerfil(payload),
    onSuccess: invalidate,
  });
}

export function useAtualizarPerfil() {
  const invalidate = useInvalidateAfterWrite();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AtualizarPerfilPayload }) =>
      atualizarPerfil(id, payload),
    onSuccess: invalidate,
  });
}

export function useExcluirPerfil() {
  const invalidate = useInvalidateAfterWrite();
  return useMutation({ mutationFn: (id: string) => excluirPerfil(id), onSuccess: invalidate });
}

export function useSalvarMatriz() {
  const invalidate = useInvalidateAfterWrite();
  return useMutation({
    mutationFn: ({ roleId, permissions }: { roleId: string; permissions: PermissionPatch[] }) =>
      salvarMatriz(roleId, permissions),
    onSuccess: invalidate,
  });
}
